/**
 * Die eine Markdown-Vorschau der App.
 *
 * Jede Stelle, die Markdown anzeigt (Editor, Hausaufgaben, Themenansicht),
 * geht durch `renderPreview` – damit sind Parser, Wiederverwendung fertiger
 * Knoten, Syntaxfarben, Mermaid/TikZ und die Sprungmarken überall dieselben.
 * Eine Neuerung am Editor landet dadurch automatisch auch in den
 * Hausaufgaben; getrennte Aufrufketten waren genau der Grund, warum dort
 * zuletzt weder Diagramme noch farbiger Code ankamen.
 */

import { renderMarkdownBlocks } from "./index";
import { updateBlocks } from "./reuse";
import { hydrateMermaid, hydrateTikz, hydrateCode } from "./diagrams";
import { svgWarmUp } from "./svgCache";
import { bindDiagramPopups } from "./diagramViewer";
import { openModal } from "@/components/ui";

// Einmal je Sitzung: gespeicherte Grafiken in den Arbeitsspeicher holen,
// damit beim ersten Rendern nichts neu gezeichnet wird.
const warmed = svgWarmUp();

export interface PreviewOptions {
  /**
   * Wartezeit vor dem Zeichnen von Mermaid/TikZ. Beim Tippen sinnvoll (die
   * kosten Netzwerk und Rechenzeit), sonst 0.
   */
  diagramDelay?: number;
  /** Nach jedem Schritt gerufen – der Editor zieht damit den Scroll-Abgleich nach. */
  onUpdated?: () => void;
}

/** Anker-Links (`#ueberschrift`) innerhalb der Vorschau springen lassen. */
function interceptAnchors(root: HTMLElement): void {
  if (root.dataset.anchorsBound) return;
  root.dataset.anchorsBound = "1";
  root.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
    if (!a || !root.contains(a)) return;
    const href = a.getAttribute("href") || "";
    if (href.length < 2) return;
    e.preventDefault();
    const id = decodeURIComponent(href.slice(1));
    const target = root.querySelector("#" + (window.CSS && CSS.escape ? CSS.escape(id) : id));
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

/**
 * Begriffsdefinitionen (`[Begriff](def: "Erklärung")`, siehe markdown/index.ts):
 * Klick/Tippen öffnet die Erklärung als eigenes Popup-Fenster statt einer
 * winzigen Hover-Box – schließt wie jeder Dialog beim Klick daneben oder
 * Escape (siehe `openModal`), bleibt also nie hängen.
 */
function bindTermPopups(root: HTMLElement): void {
  if (root.dataset.termsBound) return;
  root.dataset.termsBound = "1";
  root.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>(".md-term");
    if (!el || !root.contains(el)) return;
    e.preventDefault();
    openModal({ title: el.textContent?.trim() || "Begriff", body: el.dataset.def ?? "", guardUnsaved: false });
  });
}

const diagramTimers = new WeakMap<HTMLElement, number>();

/**
 * Zeichnet alles nach, was nach dem HTML noch fehlt: Codefarben sofort (billig
 * und beim Tippen sichtbar), Diagramme je nach `diagramDelay`.
 */
export function hydratePreview(el: HTMLElement, opts: PreviewOptions = {}): void {
  interceptAnchors(el);
  bindTermPopups(el);
  bindDiagramPopups(el);
  void warmed;
  void hydrateCode(el).then(() => opts.onUpdated?.());

  const delay = opts.diagramDelay ?? 0;
  const draw = () => {
    void Promise.all([hydrateMermaid(el), hydrateTikz(el)]).then(() => opts.onUpdated?.());
  };
  window.clearTimeout(diagramTimers.get(el));
  if (delay <= 0) draw();
  else diagramTimers.set(el, window.setTimeout(draw, delay));
}

/** Markdown rendern und fertig aufbereiten – getauscht wird nur, was sich geändert hat. */
export function renderPreview(el: HTMLElement, source: string, opts: PreviewOptions = {}): void {
  updateBlocks(el, renderMarkdownBlocks(source));
  opts.onUpdated?.();
  hydratePreview(el, opts);
}
